import "./ClockInModal.scss";
import React, { useState, useEffect } from "react";
import request from "request";
import Flash from "flash";

import Modal from "Modal";
import Button from "Button";
import Input from "Input";

const formatElapsed = (startAt, now) => {
  const diff = Math.max(0, Math.floor((now - new Date(startAt)) / 1000));
  const hours = Math.floor(diff / 3600);
  const minutes = Math.floor((diff % 3600) / 60);
  const seconds = diff % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const ClockInModal = ({ open, onClose, employees, onClockIn, onClockOut }) => {
  const [authCode, setAuthCode] = useState("");
  const [clockingIn, setClockingIn] = useState(false);
  const [clockingOutId, setClockingOutId] = useState(null);
  const [pendingClockOutId, setPendingClockOutId] = useState(null);
  const [clockOutAuthCode, setClockOutAuthCode] = useState("");
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!open) return undefined;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setPendingClockOutId(null);
      setClockOutAuthCode("");
    }
  }, [open]);

  const handleSubmit = (e = null) => {
    if (e) e.preventDefault();
    if (authCode.length !== 4) {
      Flash.broadcast("error", "Auth code must be 4 digits");
      return;
    }
    setClockingIn(true);
    request.post("terminal/clock_in", { auth_code: authCode })
      .then(({ data }) => {
        if (data.success) {
          Flash.broadcast("success", `${data.employee.name} clocked in successfully`);
          onClockIn(data.employee);
        } else {
          Flash.broadcast("error", data.error || "Failed to clock in");
        }
        setAuthCode("");
      })
      .catch(() => {
        Flash.broadcast("error", "An error occurred while clocking in");
        setAuthCode("");
      })
      .finally(() => {
        setClockingIn(false);
      });
  };

  const handleClockOutClick = (entry) => {
    if (clockingOutId) return;
    setPendingClockOutId(entry.timeLogId);
    setClockOutAuthCode("");
  };

  const handleCancelClockOut = () => {
    setPendingClockOutId(null);
    setClockOutAuthCode("");
  };

  const handleConfirmClockOut = (entry) => {
    if (clockingOutId) return;
    if (clockOutAuthCode.length !== 4) {
      Flash.broadcast("error", "Auth code must be 4 digits");
      return;
    }
    setClockingOutId(entry.timeLogId);
    request.post("terminal/clock_out", { time_log_id: entry.timeLogId, auth_code: clockOutAuthCode })
      .then(({ data }) => {
        if (data.success) {
          Flash.broadcast("success", `${entry.name} clocked out successfully`);
          onClockOut(entry.timeLogId);
          setPendingClockOutId(null);
          setClockOutAuthCode("");
        } else {
          Flash.broadcast("error", data.error || "Failed to clock out");
          setClockOutAuthCode("");
        }
      })
      .catch(() => {
        Flash.broadcast("error", "An error occurred while clocking out");
        setClockOutAuthCode("");
      })
      .finally(() => {
        setClockingOutId(null);
      });
  };

  const handleClose = () => {
    setAuthCode("");
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <div className="ClockInModal">
        <h3 className="title is-4">Clocked In</h3>

        {employees.length === 0 ? (
          <p className="subtitle is-6 mb-4">No one is clocked in at this terminal.</p>
        ) : (
          <table className="table is-fullwidth mb-4">
            <tbody>
              {employees.map((entry) => (
                <React.Fragment key={entry.timeLogId}>
                  <tr>
                    <td className="has-text-weight-bold">{entry.name}</td>
                    <td className="has-text-success">{formatElapsed(entry.startAt, now)}</td>
                    <td className="has-text-right">
                      <Button
                        className="is-danger is-small"
                        text="Clock Out"
                        loading={clockingOutId === entry.timeLogId}
                        disabled={clockingOutId !== null || (pendingClockOutId !== null && pendingClockOutId !== entry.timeLogId)}
                        onClick={() => handleClockOutClick(entry)}
                      />
                    </td>
                  </tr>
                  {pendingClockOutId === entry.timeLogId && (
                    <tr className="ClockInModal__confirmRow">
                      <td colSpan={3}>
                        <p className="help mb-2">Enter {entry.name}'s 4-digit auth code to clock out</p>
                        <div className="field has-addons">
                          <div className="control is-expanded">
                            <Input
                              type="text"
                              value={clockOutAuthCode}
                              onChange={setClockOutAuthCode}
                              placeholder="0000"
                              maxLength={4}
                              pattern="\d{4}"
                              autoFocus
                              className="has-text-centered"
                            />
                          </div>
                          <div className="control">
                            <Button
                              className="is-danger"
                              text="Confirm"
                              loading={clockingOutId === entry.timeLogId}
                              disabled={clockingOutId !== null || clockOutAuthCode.length !== 4}
                              onClick={() => handleConfirmClockOut(entry)}
                            />
                          </div>
                          <div className="control">
                            <Button
                              className=""
                              text="Cancel"
                              disabled={clockingOutId !== null}
                              onClick={handleCancelClockOut}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}

        <h4 className="title is-5 mt-5">Clock In</h4>
        <p className="subtitle is-6 mb-4">Enter your 4-digit auth code</p>

        <form onSubmit={handleSubmit}>
          <Input
            type="text"
            value={authCode}
            onChange={setAuthCode}
            placeholder="0000"
            maxLength={4}
            pattern="\d{4}"
            autoFocus
            className="is-large has-text-centered"
          />

          <div className="buttons mt-4">
            <Button
              type="submit"
              className="is-primary is-fullwidth"
              text="Clock In"
              loading={clockingIn}
              disabled={clockingIn || authCode.length !== 4}
              onClick={handleSubmit}
            />
            <Button
              type="button"
              className="is-fullwidth"
              text="Close"
              onClick={handleClose}
              disabled={clockingIn || clockingOutId !== null}
            />
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ClockInModal;
